import { useMemo, useState } from "react";
import type { DemandAnalysis } from "../../api/types";
import {
  DIRS,
  type Dir,
  type FitRow,
  type PlanParams,
  fmtHHMM,
  fmtPct,
  fmtRiders,
  headwayFor,
  niceScale,
  stationLabel,
} from "./demandMath";
import { useElementWidth } from "./useElementWidth";

interface Props {
  demand: DemandAnalysis;
  rows: FitRow[];
  /** False when there is no timetable to compare against: bars only. */
  hasSupply: boolean;
  /** "draft timetable" / "active timetable": what the capacity line is. */
  supplyLabel: string;
  plan: PlanParams;
  dirNames: Record<Dir, string>;
  /** Buckets inside [from, to) are marked as the selected time window. */
  window: [number, number] | null;
  onPickBucket: (startMin: number) => void;
}

const MARGIN = { left: 46, right: 12, top: 20, bottom: 22 };
const PLOT_H = 180;
const PANEL_H = MARGIN.top + PLOT_H + MARGIN.bottom;
const TIP_W = 260; // matches .dm-tip

/** A bar with a 4px rounded data end and a square foot on the baseline. */
function barPath(x: number, y: number, w: number, h: number): string {
  if (h <= 0 || w <= 0) return "";
  const r = Math.min(4, w / 2, h);
  return (
    `M${x},${y + h} L${x},${y + r} Q${x},${y} ${x + r},${y} ` +
    `L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} L${x + w},${y + h} Z`
  );
}

export default function FitChart({
  demand, rows, hasSupply, supplyLabel, plan, dirNames, window: win, onPickBucket,
}: Props) {
  const [wrapRef, width] = useElementWidth<HTMLDivElement>();
  const [hover, setHover] = useState<{ index: number; dir: Dir } | null>(null);
  const [asTable, setAsTable] = useState(false);

  const bucketMin = demand.bucket_minutes;
  const n = rows.length;

  const scale = useMemo(() => {
    let max = 0;
    for (const r of rows) {
      for (const dir of DIRS) {
        max = Math.max(max, r[dir].peak);
        if (hasSupply) max = Math.max(max, r[dir].capacity);
      }
    }
    return niceScale(max);
  }, [rows, hasSupply]);

  const plotW = Math.max(0, width - MARGIN.left - MARGIN.right);
  const band = n > 0 ? plotW / n : 0;
  const barW = Math.min(24, Math.max(3, band * 0.6));
  const y = (v: number) => MARGIN.top + PLOT_H - (Math.min(v, scale.max) / scale.max) * PLOT_H;
  const baseline = MARGIN.top + PLOT_H;

  // Label every bucket when there is room, else only the hours, else every other hour.
  const pxPerHour = band * (60 / bucketMin);
  const labelEvery = pxPerHour >= 34 ? 60 : pxPerHour >= 17 ? 120 : 180;
  const showMinutes = band >= 44;

  const ticks: number[] = [];
  for (let v = 0; v <= scale.max + 1e-9; v += scale.step) ticks.push(v);

  const linkName = (k: number) =>
    k >= 0 && k < demand.stops.length - 1
      ? `${stationLabel(demand.stops[k].name)} – ${stationLabel(demand.stops[k + 1].name)}`
      : "";

  const need = (r: FitRow) =>
    headwayFor(Math.max(r.upward.peak, r.downward.peak) * (60 / bucketMin), plan);

  const hovered = hover ? rows[hover.index] : null;

  // Beside the hovered bucket, never over it: to its right while there is
  // room, otherwise to its left.
  const tipLeft = (index: number) => {
    const right = MARGIN.left + (index + 1) * band + 10;
    if (right + TIP_W <= width) return right;
    return Math.max(0, MARGIN.left + index * band - 10 - TIP_W);
  };

  return (
    <div className="dm-chart">
      <div className="dm-chart-head">
        <div>
          <h4>Peak load by departure time</h4>
          <p className="dm-chart-sub">
            Riders aboard at the busiest point of the route, for trips leaving their first stop in each{" "}
            {bucketMin === 60 ? "hour" : bucketMin === 30 ? "half hour" : `${bucketMin} minutes`}
            {hasSupply ? `, against the ${supplyLabel}'s capacity.` : "."}
          </p>
        </div>
        <div className="sched-segmented dm-view-toggle" role="group" aria-label="View as">
          <button type="button" className={!asTable ? "active" : ""} onClick={() => setAsTable(false)}>Chart</button>
          <button type="button" className={asTable ? "active" : ""} onClick={() => setAsTable(true)}>Table</button>
        </div>
      </div>

      {asTable ? (
        <FitTable rows={rows} hasSupply={hasSupply} dirNames={dirNames} bucketMin={bucketMin} need={need} />
      ) : (
        <div className="dm-fit-wrap" ref={wrapRef}>
          {width > 0 && DIRS.map((dir) => {
            let peakIndex = -1;
            rows.forEach((r, i) => {
              if (r[dir].peak > 0 && (peakIndex < 0 || r[dir].peak > rows[peakIndex][dir].peak)) peakIndex = i;
            });

            let capacityPath = "";
            if (hasSupply) {
              rows.forEach((r, i) => {
                const x0 = MARGIN.left + i * band;
                const yc = y(r[dir].capacity);
                capacityPath += i === 0 ? `M${x0},${yc}` : ` V${yc}`;
                capacityPath += ` H${x0 + band}`;
              });
            }

            return (
              <div className="dm-fit-panel" key={dir}>
                <div className="dm-fit-panel-head">
                  <span className={`dm-swatch dm-swatch-${dir}`} aria-hidden />
                  <strong>{dirNames[dir]}</strong>
                  <span className="dm-fit-panel-sub">
                    {peakIndex >= 0
                      ? `peaks at ${fmtRiders(rows[peakIndex][dir].peak)} riders, ${fmtHHMM(rows[peakIndex].startMin)}–${fmtHHMM(rows[peakIndex].startMin + bucketMin)}`
                      : "no riders in this period"}
                  </span>
                </div>
                <svg width={width} height={PANEL_H} role="img"
                  aria-label={`Peak load ${dirNames[dir]} by departure time${hasSupply ? `, with the ${supplyLabel}'s capacity` : ""}`}>
                  {/* selected window and hovered bucket sit behind the marks */}
                  {win && rows.map((r, i) =>
                    r.startMin >= win[0] && r.startMin < win[1] ? (
                      <rect key={`w${i}`} className="dm-window-band"
                        x={MARGIN.left + i * band} y={MARGIN.top - 6} width={band} height={PLOT_H + 6} />
                    ) : null
                  )}
                  {hover && (
                    <rect className="dm-hover-band"
                      x={MARGIN.left + hover.index * band} y={MARGIN.top - 6} width={band} height={PLOT_H + 6} />
                  )}

                  {ticks.map((v) => (
                    <g key={v}>
                      <line className={v === 0 ? "dm-axis" : "dm-gridline"}
                        x1={MARGIN.left} x2={MARGIN.left + plotW} y1={y(v)} y2={y(v)} />
                      <text className="dm-tick" x={MARGIN.left - 8} y={y(v)} dy="0.32em" textAnchor="end">
                        {fmtRiders(v)}
                      </text>
                    </g>
                  ))}

                  {rows.map((r, i) => {
                    const side = r[dir];
                    const cx = MARGIN.left + i * band + band / 2;
                    const top = y(side.peak);
                    const over = hasSupply && side.peak > 0.5 && side.peak > side.capacity + 0.5;
                    return (
                      <g key={r.startMin}>
                        <path className={`dm-bar dm-bar-${dir}`}
                          d={barPath(cx - barW / 2, top, barW, baseline - top)} />
                        {over && (
                          <path className="dm-over" d={`M${cx - 4},${top - 4} L${cx + 4},${top - 4} L${cx},${top - 10} Z`} />
                        )}
                        {i === peakIndex && (
                          <text className="dm-bar-label" x={cx} y={top - (over ? 14 : 6)} textAnchor="middle">
                            {fmtRiders(side.peak)}
                          </text>
                        )}
                      </g>
                    );
                  })}

                  {capacityPath && <path className="dm-capacity" d={capacityPath} />}

                  {rows.map((r, i) => {
                    if (r.startMin % labelEvery !== 0) return null;
                    const cx = MARGIN.left + i * band + band / 2;
                    return (
                      <text key={r.startMin} className="dm-tick" x={cx} y={baseline + 15} textAnchor="middle">
                        {showMinutes ? fmtHHMM(r.startMin) : String(Math.floor(r.startMin / 60)).padStart(2, "0")}
                      </text>
                    );
                  })}

                  {/* one hit target per bucket, as wide as the bucket and as tall as the plot */}
                  {rows.map((r, i) => (
                    <rect key={r.startMin} className="dm-hit"
                      x={MARGIN.left + i * band} y={MARGIN.top - 6} width={band} height={PLOT_H + 6 + MARGIN.bottom}
                      tabIndex={0} role="button"
                      aria-label={`${fmtHHMM(r.startMin)} to ${fmtHHMM(r.startMin + bucketMin)}, ${dirNames[dir]}: ${fmtRiders(r[dir].peak)} riders at the busiest point${hasSupply ? `, capacity ${fmtRiders(r[dir].capacity)}` : ""}. Select this time.`}
                      onPointerEnter={() => setHover({ index: i, dir })}
                      onPointerLeave={() => setHover((h) => (h && h.index === i && h.dir === dir ? null : h))}
                      onFocus={() => setHover({ index: i, dir })}
                      onBlur={() => setHover(null)}
                      onClick={() => onPickBucket(r.startMin)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPickBucket(r.startMin); }
                      }}
                    />
                  ))}
                </svg>

                {hover && hovered && hover.dir === dir && (
                  <div className="dm-tip" role="status" style={{ left: tipLeft(hover.index), top: 30 }}>
                    <div className="dm-tip-title">
                      Trips leaving {fmtHHMM(hovered.startMin)}–{fmtHHMM(hovered.startMin + bucketMin)}
                    </div>
                    {DIRS.map((d) => {
                      const side = hovered[d];
                      return (
                        <div className="dm-tip-row" key={d}>
                          <span className={`dm-linekey dm-linekey-${d}`} aria-hidden />
                          <div>
                            <div><strong>{fmtRiders(side.peak)}</strong> riders {dirNames[d]}</div>
                            <div className="dm-tip-detail">
                              {side.peak > 0 && side.peakLink >= 0 ? `busiest ${linkName(side.peakLink)}` : "no riders"}
                              {hasSupply && (
                                <>
                                  {" · "}
                                  {side.trips === 0
                                    ? "no trips"
                                    : `${side.trips} trip${side.trips === 1 ? "" : "s"}, room for ${fmtRiders(side.capacity)}${
                                        side.loadFactor != null ? ` (${fmtPct(side.loadFactor)} full)` : ""
                                      }`}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div className="dm-tip-foot">
                      Needs a bus every {need(hovered).headway} min each way at {fmtPct(plan.targetLoad)} load
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          <div className="dm-legend">
            {DIRS.map((dir) => (
              <span className="dm-legend-item" key={dir}>
                <span className={`dm-swatch dm-swatch-${dir}`} aria-hidden /> Peak load {dirNames[dir]}
              </span>
            ))}
            {hasSupply && (
              <>
                <span className="dm-legend-item">
                  <span className="dm-linekey dm-linekey-capacity" aria-hidden /> Capacity of the {supplyLabel}
                </span>
                <span className="dm-legend-item">
                  <svg width="10" height="8" aria-hidden><path className="dm-over" d="M1,7 L9,7 L5,1 Z" /></svg>
                  Load above capacity
                </span>
              </>
            )}
            <span className="dm-legend-hint">Select a bar to focus the matrix and load profile on that time.</span>
          </div>
        </div>
      )}
    </div>
  );
}

function FitTable({
  rows, hasSupply, dirNames, bucketMin, need,
}: {
  rows: FitRow[];
  hasSupply: boolean;
  dirNames: Record<Dir, string>;
  bucketMin: number;
  need: (r: FitRow) => { headway: number; overMinimum: boolean };
}) {
  return (
    <div className="dm-table-scroll">
      <table className="data-table compact dm-table">
        <thead>
          <tr>
            <th rowSpan={2}>Departing</th>
            {DIRS.map((dir) => (
              <th key={dir} colSpan={hasSupply ? 4 : 1} className="dm-th-group">
                <span className={`dm-swatch dm-swatch-${dir}`} aria-hidden /> {dirNames[dir]}
              </th>
            ))}
            <th rowSpan={2} className="num">Needs a bus every</th>
          </tr>
          <tr>
            {DIRS.map((dir) => (
              hasSupply ? (
                [
                  <th key={`${dir}p`} className="num">Peak load</th>,
                  <th key={`${dir}t`} className="num">Trips</th>,
                  <th key={`${dir}c`} className="num">Capacity</th>,
                  <th key={`${dir}l`} className="num">Load</th>,
                ]
              ) : (
                <th key={`${dir}p`} className="num">Peak load</th>
              )
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const n = need(r);
            return (
              <tr key={r.startMin}>
                <td>{fmtHHMM(r.startMin)}–{fmtHHMM(r.startMin + bucketMin)}</td>
                {DIRS.map((dir) => {
                  const side = r[dir];
                  const over = hasSupply && side.peak > 0.5 && side.peak > side.capacity + 0.5;
                  return hasSupply ? (
                    [
                      <td key={`${dir}p`} className="num">{fmtRiders(side.peak)}</td>,
                      <td key={`${dir}t`} className="num">{side.trips}</td>,
                      <td key={`${dir}c`} className="num">{fmtRiders(side.capacity)}</td>,
                      <td key={`${dir}l`} className="num">
                        {side.loadFactor == null ? (side.peak > 0.5 ? <span className="dm-flag">no trips</span> : "–") : (
                          over ? <span className="dm-flag">▲ {fmtPct(side.loadFactor)}</span> : fmtPct(side.loadFactor)
                        )}
                      </td>,
                    ]
                  ) : (
                    <td key={`${dir}p`} className="num">{fmtRiders(side.peak)}</td>
                  );
                })}
                <td className="num">
                  {n.headway} min{n.overMinimum ? <span className="dm-flag"> ▲</span> : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
